import { joiResolver } from '@hookform/resolvers/joi';
import { useTheme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MutationStatus } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  ChangeRequestDialog,
  ContentTemplate,
  Icon,
  KeyValue,
  prepareFiles,
  Progress,
  SectionHeader,
  TextFieldForm,
  TitleHeader,
} from 'web-library';
import { FUNCTION_NAMES } from 'web-library/models/AuthorityFunctions';
import { ConfigurationType } from 'web-library/models/DropDown';
import { REQUEST_TYPES } from 'web-library/models/Request';
import { UploadedFile } from 'web-library/models/UploadedFile';
import {
  AdminDetailsResponseContentObject,
  FunctionModule,
  RequestTypeMode,
  UserAccountsObject,
} from 'web-library/models/UserGroupAccessConfiguration/UserGroupAccessConfiguration';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { stringToLongDateAndShortTime } from 'web-library/utils/DateUtils';
import { useGetUserGroupDetails } from '../../../api-refactored/user-group-access-configuration/useGetUserGroupDetails';
import { usePostCheckUserGroupName } from '../../../api-refactored/user-group-access-configuration/usePostCheckUserGroupName';
import { usePostCreateNewUserGroup } from '../../../api-refactored/user-group-access-configuration/usePostCreateNewUserGroup';
import { usePostRequestFilePresigned } from '../../../api-refactored/user-group-access-configuration/usePostRequestFilePresigned';
import { usePostSearchApprovers } from '../../../api-refactored/user-group-access-configuration/usePostSearchApprovers';
import { usePutEditUserGroup } from '../../../api-refactored/user-group-access-configuration/usePutEditUserGroup';
import FormHeader from '../../../components/form-items/FormHeader';
import TransactionLogModal from '../../../components/TransactionLogModal';
import RouteKey from '../../../routes/key';
import AddAdminModal from './AddAdminModal';
import ChangeRequestContent from './ChangeRequestContent';
import CreateEditAdminAccountDataTable from './CreateEditAdminAccountDataTable';
import { createEditUserGroupSchema } from './CreateEditUserGroupSchema';
import ViewAdminAccountDataTable from './ViewAdminAccountDataTable';

const { admin } = RouteKey.child;
const { userGroupAccessConfiguration } = RouteKey.admin.child;

const useStyles = makeStyles()((theme) => ({
  userGroupDetails: {
    marginBottom: theme.spacing(2),
  },
  keyValueContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      flexDirection: 'column',
    },
  },
  userGroupNameTextField: {
    width: '54%',
    marginBottom: theme.spacing(7),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  approverRemarksField: {
    width: '63.5%',
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  keyValue: {
    gap: theme.spacing(3),
    [theme.breakpoints.down('md')]: {},
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-around',
  },
  formHeaderContainer: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  pill: {
    marginTop: theme.spacing(0.5),
    background: theme.color.pill.pillInfoBg,
    color: theme.color.pill.pillInfoText,
    border: 'none',
    '& .MuiTypography-root': {
      color: theme.color.pill.pillInfoText,
    },
  },
  adminAccountHeaderContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: theme.spacing(-2),
    [theme.breakpoints.down('md')]: {
      marginTop: theme.spacing(-1),
    },
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
    },
  },
  addAdminButtonContainer: {
    marginLeft: theme.spacing(5),
    marginBottom: theme.spacing(2),
    display: 'inline-flex',
    cursor: 'pointer',
    '&:hover': {
      '& $icon, & $button': {
        color: theme.color.link.linkHover01,
      },
      '& $iconRemove, $buttonRemove': {
        color: theme.color.link.linkHover02,
      },
    },
    [theme.breakpoints.down('sm')]: {
      marginLeft: theme.spacing(0),
    },
  },
  button: {
    margin: 'auto',
    alignItems: 'flex-start',
    padding: 0,
    minWidth: 0,
  },
  icon: {
    margin: 'auto',
    marginTop: theme.spacing(0.8),
    marginRight: theme.spacing(0.5),
  },
}));

export const mapApproversToDropdown = (data?: ConfigurationType[]) => {
  if (data === undefined) {
    return [];
  }

  return data?.map((a) => ({
    code: a.code,
    description: a.description,
  }));
};

const displayLoadingIndicator = (
  postSearchApproversIsLoading: boolean,
  isCreate: boolean,
  isLoadingUserGroupDetails: boolean
) => {
  if (postSearchApproversIsLoading || (!isCreate && isLoadingUserGroupDetails)) {
    return (
      <ContentTemplate>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }
  return null;
};

interface CreateEditViewUserGroupProps {
  isCreate: boolean;
  isView: boolean;
}

const CreateEditViewUserGroup = ({ isCreate, isView }: CreateEditViewUserGroupProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const history = useHistory();

  const theme = useTheme();

  const { uuid } = useParams<{ uuid: string }>();

  const [openAddAdminModal, setOpenAddAdminModal] = useState<boolean>(false);

  const [selectedAdminDetails, setSelectedAdminDetails] = useState<
    AdminDetailsResponseContentObject[]
  >([]);

  const [selectedAdminUuids, setSelectedAdminUuids] = useState<string[]>(['']);

  const [openTransactionLogModal, setOpenTransactionLogModal] = useState<boolean>(false);

  const [errorMessage, setErrorMessage] = useState<string>('');

  const [uploadFileErrorMessage, setUploadFileErrorMessage] = useState<string>('');

  const [confirmChangesModal, setConfirmChangesModal] = useState<boolean>(false);

  const [apiButtonStatus, setApiButtonStatus] = useState<MutationStatus>('idle');

  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [shouldSubmit, setShouldSubmit] = useState<boolean>(false);

  const { mutate: mutatePostCheckGroupName } = usePostCheckUserGroupName({
    onSuccess: () => {
      setConfirmChangesModal(false);
      setErrorMessage(t('admin->>UserGroups.UserGroupAlreadyExistErrorMessage'));
    },
    onError: () => setConfirmChangesModal(true),
  });

  const handleOpenAddAdminModal = () => setOpenAddAdminModal(true);

  const { data: userGroupDetails, isLoading: isLoadingUserGroupDetails } = useGetUserGroupDetails({
    uuid,
    enabled: !isCreate,
    onSuccess: (res) => {
      if (res.userGroupDetail.userAccounts) {
        const tempArray: AdminDetailsResponseContentObject[] = [];
        const initialSelectedAdminUuid: string[] = [];
        res.userGroupDetail.userAccounts.forEach((userAccount) => {
          tempArray.push({
            name: userAccount.name,
            email: userAccount.email,
            username: userAccount.username,
            userUuid: userAccount.uuid,
            isEnabled: false,
          });
          initialSelectedAdminUuid.push(userAccount.uuid);
        });
        setSelectedAdminDetails(tempArray);
        setSelectedAdminUuids(initialSelectedAdminUuid);
      }
    },
  });

  const formHook = useForm({
    mode: 'onSubmit',
    resolver: joiResolver(createEditUserGroupSchema()),
  });

  const {
    formState: { errors },
    handleSubmit,
    getValues,
    register,
  } = formHook;

  const {
    mutate: mutatePostSearchApprovers,
    data: postSearchApproversData,
    isLoading: postSearchApproversIsLoading,
  } = usePostSearchApprovers({});

  const handleCreateEditBackToListing = () =>
    history.push(`/${admin}/${userGroupAccessConfiguration.route}`, {
      requestType: RequestTypeMode.AdminUserGroup,
      status: 'success',
      event: 'ADD',
    });

  const { mutate: mutatePostCreateNewUserGroup } = usePostCreateNewUserGroup({
    onSuccess: () => {
      handleCreateEditBackToListing();
      handleCloseConfirmChangesModal();
    },
    onError: () => {
      handleCloseConfirmChangesModal();
    },
  });

  const { mutate: postRequestFilePresigned } = usePostRequestFilePresigned({
    onSuccess: (data) => {
      const tempFiles: UploadedFile[] = [];
      data.forEach((_value, index) => {
        const pendingFile = files[index];
        const fileData = data[index];
        pendingFile.presignedUrl = fileData.presignedUrl;
        pendingFile.key = fileData.key;
        tempFiles.push(pendingFile);
      });
      setFiles(tempFiles);
    },
    onError: (err) => {
      setUploadFileErrorMessage(err?.response?.data.message!);
      setApiButtonStatus('error');
    },
  });

  const { mutate: mutatePutEditUserGroup } = usePutEditUserGroup({
    uuid,
    onSuccess: () => {
      handleCreateEditBackToListing();
      handleCloseConfirmChangesModal();
    },
    onError: () => {
      setErrorMessage(t('admin->>UserGroups.PendingRequestError'));
      setApiButtonStatus('error');
    },
  });

  useEffect(() => {
    const isAllFilesUploaded = files.every((f) => f.uploaded);
    const fileKeys: string[] = [];
    files.forEach((f) => {
      if (f.key) {
        fileKeys.push(f.key);
      }
    });

    const fileUploadHasError = files.some((f) => f.hasError);
    if (fileUploadHasError) {
      setApiButtonStatus('error');
      setShouldSubmit(false);
    }

    if (isAllFilesUploaded && shouldSubmit) {
      const userAccounts: UserAccountsObject[] = [];

      selectedAdminDetails.forEach((selectedAdmin) => {
        userAccounts.push({
          uuid: selectedAdmin.userUuid,
          name: selectedAdmin.name,
          username: selectedAdmin.username,
          email: selectedAdmin.email,
        });
      });
      const createNewUserGroupPayload = {
        userGroupDetail: {
          uuid,
          userGroupDisplayName: getValues('userGroupName'),
          userAccounts,
        },
        requestDetail: {
          requestorRemarks: getValues('remarks'),
          approverUuid: getValues('approver')?.code,
          fileKeys,
        },
      };
      if (isCreate) {
        mutatePostCreateNewUserGroup(createNewUserGroupPayload);
      }
      if (!isCreate && !isView && uuid) {
        mutatePutEditUserGroup(createNewUserGroupPayload);
      }
    }
  }, [
    files,
    getValues,
    isCreate,
    isView,
    mutatePostCreateNewUserGroup,
    mutatePutEditUserGroup,
    selectedAdminDetails,
    shouldSubmit,
    uuid,
  ]);

  useEffect(() => {
    if (selectedAdminDetails) {
      setErrorMessage('');
    }
  }, [selectedAdminDetails]);
  useEffect(() => {
    mutatePostSearchApprovers({
      functionName: FUNCTION_NAMES.ADMIN_USER_GROUP,
      functionModule: FunctionModule.ADMIN,
    });
  }, [mutatePostSearchApprovers]);

  displayLoadingIndicator(postSearchApproversIsLoading, isCreate, isLoadingUserGroupDetails);

  const handleOpenConfirmChangesModal = () => {
    if (errorMessage || uploadFileErrorMessage) {
      setConfirmChangesModal(false);
    } else if (
      isCreate ||
      getValues('userGroupName') !== userGroupDetails?.userGroupDetail.userGroupDisplayName
    ) {
      mutatePostCheckGroupName({
        q: getValues('userGroupName'),
      });
    } else {
      setConfirmChangesModal(true);
    }
  };

  const handleCloseConfirmChangesModal = () => setConfirmChangesModal(false);

  const handleConfirmChangesModal = () => {
    const { filenames } = prepareFiles(files, setFiles);
    setShouldSubmit(true);
    setApiButtonStatus('loading');

    if (filenames.length) {
      setConfirmChangesModal(false);
      postRequestFilePresigned({
        filenames,
        uuid,
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(() => handleOpenConfirmChangesModal())}>
      <TitleHeader
        title={t('admin->>UserGroups.UserGroup', {
          userGroup:
            (userGroupDetails && userGroupDetails.userGroupDetail.userGroupDisplayName) ||
            'New User Group',
        })}
      />
      <ContentTemplate isDividerBottom isContinuable>
        <SectionHeader title={t('admin->>UserGroups.UserGroupDetails')} />
        {!isView && (isCreate || (!isCreate && userGroupDetails)) && (
          <TextFieldForm
            label={t('admin->>UserGroups.UserGroupName')}
            isRequired
            name="userGroupName"
            inputProps={{
              maxLength: 200,
            }}
            defaultValue={
              isCreate
                ? ''
                : userGroupDetails && userGroupDetails.userGroupDetail.userGroupDisplayName
            }
            placeholder={t('admin->>UserGroups.UserGroupNamePlaceholder')}
            className={classes.userGroupNameTextField}
            error={!!errors.userGroupName?.message}
            helperText={errors.userGroupName?.message?.toString()}
            form={formHook}
          />
        )}
        {isView && userGroupDetails && (
          <KeyValue
            keyText={t('admin->>UserGroups.UserGroupName')}
            valueText={userGroupDetails.userGroupDetail.userGroupDisplayName}
          />
        )}
        {!isCreate && userGroupDetails && (
          <Box className={classes.userGroupDetails}>
            <Box className={classes.keyValueContainer}>
              <Box className={classes.keyValue}>
                <KeyValue
                  keyText={t('admin->>UserGroups.LastUpdatedDateTime')}
                  valueText={stringToLongDateAndShortTime(
                    userGroupDetails.userGroupDetail.updatedAt
                  )}
                />
              </Box>
              <KeyValue
                capitalize={false}
                keyText={t('admin->>UserGroups.LastUpdatedBy')}
                valueText={userGroupDetails.userGroupDetail.updatedBy}
              />
            </Box>
            <Box className={classes.keyValueContainer}>
              <Box className={classes.keyValue}>
                <KeyValue
                  keyText={t('admin->>UserGroups.CreatedDateTime')}
                  valueText={stringToLongDateAndShortTime(
                    userGroupDetails.userGroupDetail.createdAt
                  )}
                />
              </Box>
              <KeyValue
                capitalize={false}
                keyText={t('admin->>UserGroups.CreatedBy')}
                valueText={userGroupDetails.userGroupDetail.createdBy}
              />
            </Box>
          </Box>
        )}
      </ContentTemplate>
      <ContentTemplate type="full" isDividerBottom={!isView} isContinuable continuous>
        <Box className={classes.adminAccountHeaderContainer}>
          <Box className={classes.formHeaderContainer}>
            <FormHeader title={t('admin->>UserGroups.AdminAccounts')} />
            <Avatar className={classes.pill} type="text">
              {selectedAdminDetails.length}
            </Avatar>
          </Box>
          {!isView && (
            <Box className={classes.addAdminButtonContainer}>
              <Button
                id="button-addAdmin"
                className={classes.button}
                size="large"
                startIcon={
                  <Icon
                    size="M"
                    className={classes.icon}
                    name={IconName.add}
                    color={theme.color.icon.iconSelected}
                  />
                }
                onClick={handleOpenAddAdminModal}
                variant="text"
                color="primary"
              >
                {t('admin->>UserGroups.AddAdmin')}
              </Button>
            </Box>
          )}
        </Box>
        {!isView && (
          <CreateEditAdminAccountDataTable
            selectedAdminDetails={selectedAdminDetails}
            setSelectedAdminDetails={setSelectedAdminDetails}
            setSelectedAdminUuids={setSelectedAdminUuids}
          />
        )}
        {isView && userGroupDetails && (
          <ViewAdminAccountDataTable userGroupDetails={userGroupDetails.userGroupDetail} />
        )}
      </ContentTemplate>
      <ContentTemplate continuous type="full">
        <ChangeRequestContent
          isCreate={isCreate}
          isView={isView}
          formHook={formHook}
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
          uploadFileErrorMessage={uploadFileErrorMessage}
          selectedAdminDetails={selectedAdminDetails}
          setOpenTransactionLogModal={setOpenTransactionLogModal}
          userGroupDetails={userGroupDetails}
          postSearchApproversData={postSearchApproversData}
          files={files}
          setFiles={setFiles}
          apiButtonStatus={apiButtonStatus}
        />
      </ContentTemplate>
      <AddAdminModal
        modalState={openAddAdminModal}
        setOpenModalState={setOpenAddAdminModal}
        selectedAdminUuids={selectedAdminUuids}
        setSelectedAdminUuids={setSelectedAdminUuids}
        setSelectedAdminDetails={setSelectedAdminDetails}
        userGroupDetails={userGroupDetails}
      />
      <TransactionLogModal
        modalState={openTransactionLogModal}
        setOpenModalState={setOpenTransactionLogModal}
        requestType={REQUEST_TYPES.USER_GROUP}
      />
      <ChangeRequestDialog
        open={confirmChangesModal}
        onClose={() => setConfirmChangesModal(false)}
        onConfirm={handleConfirmChangesModal}
      />
    </form>
  );
};

export default CreateEditViewUserGroup;
